import GetUserPermissionsApi from '../api/position-features/GetUserPermissionsApi';

class PermissionService {
  // Cache duration in milliseconds (disabled for debugging)
  static CACHE_DURATION = 0;

  constructor() {
    this.permissions = null;
    this.loading = false;
    this.lastFetch = null;
  }

  async getUserPermissions(positionId = null) {
    // Get current position from localStorage if not provided
    const currentPositionId = positionId || localStorage.getItem('currentPositionId');
    
    // Create cache key based on positionId
    const cacheKey = currentPositionId || 'default';
    
    // Return cached permissions if available and not expired
    if (this.permissions && this.permissions[cacheKey] && this.lastFetch && 
        (Date.now() - this.lastFetch) < PermissionService.CACHE_DURATION) {
      return this.permissions[cacheKey];
    }

    // Prevent multiple simultaneous requests
    if (this.loading) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!this.loading) {
            resolve(this.permissions ? this.permissions[cacheKey] : null);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    this.loading = true;
    try {
      const response = await GetUserPermissionsApi(currentPositionId);
      if (response.success) {
        // Initialize permissions object if needed
        if (!this.permissions) {
          this.permissions = {};
        }
        this.permissions[cacheKey] = response.data;
        this.lastFetch = Date.now();
      } else {
        // Default to all permissions enabled if API fails
        if (!this.permissions) {
          this.permissions = {};
        }
        this.permissions[cacheKey] = {
          prediction: true,
          timeline: true,
          timesheet: true,
          subtargets: true,
          chatbot: true,
          zoho: false,
          odoo: false,
          phrm: false,
          gem: true,
          planApproval: true,
          targetApproval: true,
        };
      }
    } catch (error) {
      console.error("Error fetching user permissions:", error);
      // Default to all permissions enabled if API fails
      if (!this.permissions) {
        this.permissions = {};
      }
      this.permissions[cacheKey] = {
        prediction: true,
        timeline: true,
        timesheet: true,
        subtargets: true,
        chatbot: true,
        zoho: false,
        odoo: false,
        phrm: false,
        gem: true,
        planApproval: true,
        targetApproval: true,
      };
    } finally {
      this.loading = false;
    }

    return this.permissions[cacheKey];
  }

  async hasPermission(featureName, positionId = null) {
    const permissions = await this.getUserPermissions(positionId);
    return permissions[featureName] !== false;
  }

  async hasPredictionPermission() {
    return await this.hasPermission('prediction');
  }

  async hasTimelinePermission() {
    return await this.hasPermission('timeline');
  }

  async hasTimesheetPermission() {
    return await this.hasPermission('timesheet');
  }

  async hasChatbotPermission() {
    return await this.hasPermission('chatbot');
  }

  async hasZohoPermission() {
    return await this.hasPermission('zoho');
  }

  async hasOdooPermission() {
    return await this.hasPermission('odoo');
  }

  // Clear cache when user logs out or changes position
  clearCache(positionId = null) {
    if (positionId) {
      // Clear cache for specific position
      if (this.permissions && this.permissions[positionId]) {
        delete this.permissions[positionId];
      }
    } else {
      // Clear all cache
      this.permissions = null;
    }
    this.lastFetch = null;
    this.loading = false;
    
    // Trigger a custom event to notify components to refresh
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('permissionsUpdated'));
    }
  }

  // Force refresh permissions
  async refreshPermissions(positionId = null) {
    this.clearCache(positionId);
    return await this.getUserPermissions(positionId);
  }
}

// Export singleton instance
export default new PermissionService();
